import { DocumentForDocumentManager } from "@src/components/modules/DocumentManager/DocumentManager";
import { FileChangeTrigger } from "@src/components/modules/DocumentManager/DocumentManagerRoot";
import { useEffect, useState, useRef } from "react";
import { useDeleteDocument } from "@src/pages/document/DocumentDetails/useDeleteDocument";

type Args = {
  onDocsChanged?: (
    docs: (DocumentForDocumentManager & {
      selected?: boolean;
    })[],
  ) => void;
  onDocRemoved?: (doc: DocumentForDocumentManager) => void;
  onDocsAdded?: (docs: DocumentForDocumentManager[]) => void;
  existingDocs: DocumentForDocumentManager[];
};

export function useDocumentManager({
  existingDocs,
  onDocsChanged,
  onDocRemoved,
  onDocsAdded,
}: Args) {
  const fileChangeTriggerRef = useRef<FileChangeTrigger>(null);
  // Attached docs can be either docs that were previously attached, docs
  // selected from search results, or docs freshly uploaded.
  const [attachedDocs, setAttachedDocs] =
    useState<DocumentForDocumentManager[]>(existingDocs);

  // Keep track of docs uploaded in this current instance.
  // When these are removed, we delete the uploaded version.
  const [recentlyAddedDocs, setRecentlyAddedDocs] = useState<
    DocumentForDocumentManager[]
  >([]);

  const { deleteDocument, deleteDocumentLoading, deleteDocumentResponse } =
    useDeleteDocument();

  function addDocs(
    newDocs: DocumentForDocumentManager[],
    shouldAddToRecentlyAdded = true,
  ) {
    const updatedDocs = [...attachedDocs, ...newDocs];
    setAttachedDocs(updatedDocs);
    onDocsChanged?.(updatedDocs);
    onDocsAdded?.(newDocs);
    if (shouldAddToRecentlyAdded) {
      setRecentlyAddedDocs((docs) => [...docs, ...newDocs]);
    }
  }

  async function removeDoc(doc: DocumentForDocumentManager) {
    const updatedDocs = attachedDocs.filter((d) => d.id !== doc.id);
    setAttachedDocs(updatedDocs);
    onDocsChanged?.(updatedDocs);
    onDocRemoved?.(doc);
    fileChangeTriggerRef.current?.clear();
    if (recentlyAddedDocs.map((d) => d.id).includes(doc.id)) {
      await deleteDocument({
        variables: {
          id: doc.id,
        },
      });
    }
  }

  useEffect(() => {
    if (existingDocs.length) {
      setAttachedDocs(existingDocs);
    }
  }, [existingDocs]);

  return {
    attachedDocs,
    addDocs,
    removeDoc,
    deleteDocumentLoading,
    deleteDocumentResponse,
    fileChangeTriggerRef,
  };
}
